import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FaHome, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import useMyDashboards from '../../hooks/useMyDashboards';
import { dashboardPath, isDashboardPathname, sameDashboardId } from '../../utils/dashboards';

// Sidebar "Dashboards" entry: a dropdown of the dashboards this user may open. Each child is a
// real link (not just an onClick) so it can be opened in its own browser tab.
const DashboardsNavItem = ({ isCollapsed }) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { dashboards } = useMyDashboards();
	const onDashboardPage = isDashboardPathname(pathname);
	const [open, setOpen] = useState(onDashboardPage);

	const handleParentClick = () => {
		// Collapsed sidebar has no room for the list: go straight to the user's first dashboard.
		if (isCollapsed) {
			navigate('/home');
			return;
		}
		setOpen((prev) => !prev);
	};

	const showChildren = open && !isCollapsed;
	const Chevron = showChildren ? FaChevronDown : FaChevronRight;

	return (
		<Nav.Item>
			<Nav.Link
				onClick={handleParentClick}
				title="Dashboards"
				aria-expanded={showChildren}
				className={`sidebar-nav-link d-flex align-items-center px-3 py-3 border-0 ${
					onDashboardPage ? 'sidebar-nav-active text-white fw-bold' : 'text-dark hover-bg-light'
				}`}
			>
				<FaHome size={16} className={isCollapsed ? 'mx-auto' : 'me-3'} />
				{!isCollapsed && (
					<>
						<span className="fw-medium flex-grow-1">Dashboards</span>
						<Chevron size={10} aria-hidden="true" />
					</>
				)}
			</Nav.Link>
			{showChildren && (
				<ul className="sidebar-dashboards-list" aria-label="Dashboards">
					{dashboards && dashboards.length === 0 && (
						<li className="sidebar-dashboards-empty">No dashboards available</li>
					)}
					{(dashboards ?? []).map((dashboard) => {
						const path = dashboardPath(dashboard.id);
						const active = pathname.startsWith('/dashboards/')
							&& sameDashboardId(pathname.slice('/dashboards/'.length), dashboard.id);
						return (
							<li key={dashboard.id}>
								<NavLink
									to={path}
									className={`sidebar-dashboards-link${active ? ' sidebar-dashboards-link--active' : ''}`}
									aria-current={active ? 'page' : undefined}
								>
									{dashboard.name}
								</NavLink>
							</li>
						);
					})}
				</ul>
			)}
		</Nav.Item>
	);
};

DashboardsNavItem.propTypes = {
	isCollapsed: PropTypes.bool.isRequired,
};

export default DashboardsNavItem;
